import { toast, useApiClient } from '@startupquickstart/frontend';
import { useEffect, useState } from 'react';

export interface AmenityTypeRef {
  id: string;
  name: string;
}

export interface HotelRef {
  id: string;
  name: string;
}

export interface AmenityFormData {
  name: string;
  amenityTypeId: string;
  hotelId: string;
  status: 'published' | 'draft' | 'needs_review';
  featuredImageId?: string;
  description?: string;
  phoneNumber?: string;
  email?: string;
  website?: string;
  usesHotelAddress: boolean;
  floor?: string;
  capacity?: number;
  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  hours?: Record<string, unknown>;
  amenityType?: AmenityTypeRef;
  hotel?: HotelRef;
}

const EMPTY_FORM: AmenityFormData = {
  name: '',
  amenityTypeId: '',
  hotelId: '',
  status: 'published',
  usesHotelAddress: false,
};

export interface UseAmenityFormOptions {
  /** Existing amenity id when editing; omit when creating. */
  amenityId?: string;
  /** Hotel to pre-select when creating a new amenity. */
  hotelId?: string;
  /** Called with the saved amenity's id after a successful create/update. */
  onSuccess: (amenityId: string) => void;
}

export interface UseAmenityFormResult {
  formData: AmenityFormData;
  setFormData: React.Dispatch<React.SetStateAction<AmenityFormData>>;
  isLoading: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  handleSubmit: () => Promise<void>;
}

/**
 * Owns the create/edit state for an amenity form: pre-fills defaults, loads an
 * existing amenity when editing, and persists changes. Layout-agnostic so it can
 * back both the standalone amenity form page and the hotel-embedded form tab.
 *
 * @param options - Edit target, optional hotel to pre-select, and success callback.
 * @returns Form state plus loading/saving flags and a submit handler.
 */
export function useAmenityForm({
  amenityId,
  hotelId,
  onSuccess,
}: UseAmenityFormOptions): UseAmenityFormResult {
  const apiClient = useApiClient();
  const [formData, setFormData] = useState<AmenityFormData>(EMPTY_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  /**
   * Pre-fill defaults when creating a new amenity (amenity type "General" and
   * the provided hotel). Skipped entirely in edit mode.
   */
  useEffect(() => {
    if (amenityId) return;

    // Default amenity type to "General"
    apiClient
      .get<{ data: AmenityTypeRef[] }>('/amenity-types', {
        params: { search: 'General', limit: 1 },
      })
      .then(response => {
        const general = response.data.find(t => t.name.toLowerCase() === 'general');
        if (general) {
          setFormData(prev => ({
            ...prev,
            amenityTypeId: prev.amenityTypeId || general.id,
            amenityType: prev.amenityType || { id: general.id, name: general.name },
          }));
        }
      })
      .catch(() => {});

    if (!hotelId) return;

    setFormData(prev => ({ ...prev, hotelId }));
    apiClient
      .get<{ data: HotelRef }>(`/hotels/${hotelId}`)
      .then(response => {
        setFormData(prev => ({
          ...prev,
          hotelId,
          hotel: { id: response.data.id, name: response.data.name },
        }));
      })
      .catch(() => {
        // Hotel fetch failed, keep hotelId set but no initialItem
      });
  }, [amenityId, hotelId, apiClient]);

  /**
   * Load existing amenity data when editing.
   */
  useEffect(() => {
    if (!amenityId) return;

    setIsLoading(true);
    apiClient
      .get<{ data: AmenityFormData & { updatedAt?: string } }>(`/amenities/${amenityId}`)
      .then(response => {
        const data = response.data;
        setFormData({
          name: data.name || '',
          amenityTypeId: data.amenityTypeId || '',
          hotelId: data.hotelId || '',
          status: data.status || 'published',
          featuredImageId: data.featuredImageId,
          description: data.description,
          phoneNumber: data.phoneNumber,
          email: data.email,
          website: data.website,
          usesHotelAddress: data.usesHotelAddress || false,
          floor: data.floor,
          capacity: data.capacity,
          streetAddress: data.streetAddress,
          streetAddress2: data.streetAddress2,
          city: data.city,
          state: data.state,
          postalCode: data.postalCode,
          country: data.country,
          hours: data.hours,
          amenityType: data.amenityType,
          hotel: data.hotel,
        });
        if (data.updatedAt) {
          setLastSaved(new Date(data.updatedAt));
        }
      })
      .catch(error => {
        toast.error((error as Error)?.message || 'Failed to load amenity');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [amenityId, apiClient]);

  /**
   * Persist the amenity, then hand the saved id back to the caller for navigation.
   */
  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const endpoint = amenityId ? `/amenities/${amenityId}` : '/amenities';
      const method = amenityId ? 'put' : 'post';

      const result = await apiClient[method]<{ data: { id: string } }>(endpoint, formData);

      setLastSaved(new Date());
      toast.success(amenityId ? 'Amenity updated successfully' : 'Amenity created successfully');
      onSuccess(result.data.id);
    } catch (error) {
      toast.error((error as Error)?.message || 'Failed to save amenity');
    } finally {
      setIsSaving(false);
    }
  };

  return { formData, setFormData, isLoading, isSaving, lastSaved, handleSubmit };
}
